import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GitBranch, GitFork, Lightbulb, History } from 'lucide-react';
import { Button } from '../ui/Button';
interface BranchCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceNode: string;
  onCreate: (data: { type: string; name: string; description: string }) => void;
}
export function BranchCreationModal({
  isOpen,
  onClose,
  sourceNode,
  onCreate
}: BranchCreationModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'exploratory' | 'alternative' | 'rollback'>('exploratory');
  const [description, setDescription] = useState('');
  if (!isOpen) return null;
  const handleCreate = () => {
    onCreate({
      name,
      type,
      description
    });
    onClose();
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div initial={{
      scale: 0.95,
      y: 20
    }} animate={{
      scale: 1,
      y: 0
    }} className="w-full max-w-lg bg-surface-900 border border-surface-600 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-surface-600">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
              <GitBranch className="w-4 h-4 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">Create Branch</h2>
          </div>
          <p className="text-sm text-text-secondary">
            Branching from{' '}
            <span className="text-text-primary font-medium">{sourceNode}</span>
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Branch Type */}
          <div className="grid grid-cols-3 gap-3">
            {[{
            id: 'exploratory',
            label: 'Exploratory',
            icon: Lightbulb,
            desc: 'Testing a hypothesis'
          }, {
            id: 'alternative',
            label: 'Alternative',
            icon: GitFork,
            desc: 'Different solution'
          }, {
            id: 'rollback',
            label: 'Rollback',
            icon: History,
            desc: 'Safe point'
          }].map(opt => <button key={opt.id} onClick={() => setType(opt.id as 'exploratory' | 'alternative' | 'rollback')} className={`flex flex-col items-center text-center p-3 rounded-lg border transition-all ${type === opt.id ? 'bg-purple-500/10 border-purple-500 text-purple-400' : 'bg-surface-800/50 border-surface-600 text-text-secondary hover:bg-surface-800'}`}>
                <opt.icon className="w-5 h-5 mb-2" />
                <span className="text-xs font-medium mb-1">{opt.label}</span>
                <span className="text-[10px] opacity-70">{opt.desc}</span>
              </button>)}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-text-primary mb-2 block">
                Branch Name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., try-redis-approach" className="w-full bg-surface-800 border border-surface-500 rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-text-primary mb-2 block">
                Description
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What are you exploring in this branch?" className="w-full bg-surface-800 border border-surface-500 rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 h-24 resize-none" />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/30 border border-surface-600 cursor-pointer hover:bg-surface-800/50">
              <input type="checkbox" className="rounded border-surface-500 bg-surface-900 text-purple-500 focus:ring-purple-500" />
              <div className="text-sm">
                <div className="text-text-primary font-medium">
                  Copy downstream tasks
                </div>
                <div className="text-text-muted text-xs">
                  Include pending tasks from main path
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-surface-600 bg-surface-900/50 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name} onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700 text-text-primary border-none">
            Create Branch
          </Button>
        </div>
      </motion.div>
    </motion.div>;
}